import { Link } from 'react-router-dom';

import { buildIntegrationPrompt } from '@admin-platform/shared/integration-content';
import { AnchorHeading } from '../components/AnchorHeading';
import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';
import { useDocsPage } from '../hooks/useDocsPage';

const AGENT_PROMPT = buildIntegrationPrompt('supportWidget');

const BACKEND = `// server.js (Express). The secret key lives here and nowhere else.
import express from 'express';

const app = express();

// One token per user, reused until a minute before it expires, so page loads
// and tabs share it instead of each minting their own. Use Redis instead of a
// Map when you run more than one server. Never write tokens to a database.
const tokens = new Map(); // cache key -> { token, expiresAt }

// requireSignedInUser is your own auth middleware.
app.post('/api/support-token', requireSignedInUser, async (req, res) => {
  const user = req.user; // from YOUR session, never from the request body
  // Everything you send as claims. The cache key is this very object, so a
  // changed name, email, role or plan mints a new token, and a claim you add
  // later is covered without touching the key.
  const input = {
    externalUserId: user.id,
    name: user.name,
    email: user.email, // lets Revvly email replies the user has not read
    organizationExternalId: user.accountId, // optional: links requests to the client
  };
  const key = JSON.stringify(input);
  const cached = tokens.get(key);
  const fresh = req.query.refresh === '1'; // the client asks after UNAUTHENTICATED
  if (cached && !fresh && new Date(cached.expiresAt) - Date.now() > 60_000) {
    return res.json(cached);
  }
  const response = await fetch('https://api.revvly.ai/api/v1/graphql', {
    method: 'POST',
    headers: {
      Authorization: \`Bearer \${process.env.REVVLY_SUPPORT_SECRET_KEY}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: \`mutation($input: EndUserClaimsInput!) {
        createEndUserToken(input: $input) { token expiresAt }
      }\`,
      variables: { input },
    }),
  });
  const { data, errors } = await response.json();
  if (errors) return res.status(502).json({ error: errors[0].extensions?.code });
  tokens.set(key, data.createEndUserToken);
  res.json(data.createEndUserToken); // { token, expiresAt }
});`;

const CLIENT = `// support-chat.js: talks to the support chat API with the user's token.
const CHAT_URL = 'https://api.revvly.ai/api/v1/support/graphql';
let session = null; // { token, expiresAt }, kept in memory only

async function token(refresh = false) {
  if (refresh || !session || new Date(session.expiresAt) - Date.now() < 60_000) {
    const res = await fetch(\`/api/support-token\${refresh ? '?refresh=1' : ''}\`, { method: 'POST' });
    if (!res.ok) throw new Error('Could not start the support chat');
    session = await res.json();
  }
  return session.token;
}

export async function chat(query, variables = {}, retried = false) {
  const res = await fetch(CHAT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: \`Bearer \${await token(retried)}\`,
    },
    body: JSON.stringify({ query, variables }),
  });
  const { data, errors } = await res.json();
  const code = errors?.[0]?.extensions?.code;
  if (code === 'UNAUTHENTICATED' && !retried) {
    // Expired or revoked: get a newly minted token, past the server's cache, and retry once.
    return chat(query, variables, true);
  }
  if (errors) throw Object.assign(new Error(errors[0].message), { code });
  return data;
}`;

const WIDGET_HTML = `<!-- Anywhere in your signed-in pages -->
<section id="support" hidden>
  <header>
    <strong>Support</strong>
    <button type="button" id="support-new">New request</button>
  </header>
  <ul id="support-list"></ul>
  <form id="support-open" hidden>
    <select id="support-type" required></select>
    <input id="support-title" maxlength="200" placeholder="Subject (optional)" />
    <textarea id="support-first" required placeholder="How can we help?"></textarea>
    <button type="submit">Send request</button>
  </form>
  <div id="support-thread" hidden>
    <ol id="support-messages"></ol>
    <p id="support-typing" hidden>Support is typing…</p>
    <form id="support-reply">
      <textarea id="support-body" required placeholder="Write a reply"></textarea>
      <button type="submit">Send</button>
    </form>
  </div>
</section>
<button type="button" id="support-toggle">Help</button>

<style>
  /* 16px or more on anything that takes text, or mobile Safari zooms in on focus. */
  #support input, #support select, #support textarea { font-size: 16px; }
  #support-messages { list-style: none; padding: 0; }
  #support-messages .you { text-align: right; }
  #support-messages p { white-space: pre-wrap; margin: 4px 0; }
</style>`;

const WIDGET_JS = `// support-widget.js
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { chat } from './support-chat.js';

const $ = (id) => document.getElementById(id);
let openId = null;
let stream = null;
let lastEventId = null;
let typingTimer = null;

const LIST = \`query { myTickets(first: 20) { nodes {
  id number title type { name } status { name category } unreadCount } } }\`;
const THREAD = \`query($id: ID!) { ticketMessages(ticketId: $id, first: 50) {
  nodes { id author body format notice createdAt } } }\`;

// AI and team replies are a small Markdown subset (format: MARKDOWN). Render them with a
// sanitiser that allows only those tags and https links; everything else stays text.
const MARKDOWN = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'code', 'pre', 'blockquote', 'ul', 'ol', 'li', 'a'],
  ALLOWED_ATTR: ['href'],
  ALLOWED_URI_REGEXP: /^https:/i,
};
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

function renderBody(el, message) {
  if (message.format === 'MARKDOWN') {
    el.innerHTML = DOMPurify.sanitize(marked.parse(message.body, { async: false }), MARKDOWN);
  } else {
    el.textContent = message.body; // plain text: never innerHTML
  }
}

function renderList(tickets) {
  $('support-list').replaceChildren(...tickets.map((t) => {
    const li = document.createElement('li');
    const link = document.createElement('a');
    link.href = '#';
    // textContent, never innerHTML: titles and messages are user text.
    link.textContent = \`\${t.number} \${t.title ?? t.type.name} · \${t.status.name}\` +
      (t.unreadCount ? \` (\${t.unreadCount} new)\` : '');
    link.onclick = (e) => { e.preventDefault(); openThread(t.id); };
    li.append(link);
    return li;
  }));
}

function renderThread(messages) {
  $('support-messages').replaceChildren(...messages.map((m) => {
    const li = document.createElement('li');
    // The AI handed the request to your team: show a notice, not a chat bubble.
    if (m.notice === 'AI_HANDOVER') {
      li.className = 'notice';
      li.textContent = 'A teammate will reply.';
      return li;
    }
    li.className = m.author === 'YOU' ? 'you' : 'team';
    const who = document.createElement('small');
    who.textContent = m.author === 'YOU' ? 'You' : m.author === 'AI' ? 'Assistant' : 'Support';
    const body = document.createElement('div');
    renderBody(body, m);
    li.append(who, body);
    return li;
  }));
}

async function refresh() {
  const { myTickets } = await chat(LIST);
  renderList(myTickets.nodes);
  if (!openId) return;
  const { ticketMessages } = await chat(THREAD, { id: openId });
  renderThread(ticketMessages.nodes);
  const open = myTickets.nodes.find((t) => t.id === openId);
  if (open?.unreadCount > 0) {
    await chat('mutation($id: ID!) { markRead(ticketId: $id) { id } }', { id: openId });
  }
}

async function openThread(id) {
  openId = id;
  $('support-open').hidden = true;
  $('support-thread').hidden = false;
  await refresh();
}

$('support-new').onclick = async () => {
  const { ticketTypes } = await chat('query { ticketTypes { id name } }');
  $('support-type').replaceChildren(...ticketTypes.map((t) => new Option(t.name, t.id)));
  openId = null;
  $('support-thread').hidden = true;
  $('support-open').hidden = false;
};

$('support-open').onsubmit = async (e) => {
  e.preventDefault();
  const { createTicket } = await chat(
    \`mutation($input: CreateTicketInput!) { createTicket(input: $input) { ticket { id } } }\`,
    { input: {
      typeId: $('support-type').value,
      title: $('support-title').value || null,
      body: $('support-first').value,
      clientMessageId: crypto.randomUUID(), // a retry with the same id never posts twice
    } },
  );
  $('support-open').reset();
  await openThread(createTicket.ticket.id);
};

$('support-reply').onsubmit = async (e) => {
  e.preventDefault();
  await chat(
    \`mutation($input: SendMessageInput!) { sendMessage(input: $input) { id } }\`,
    { input: { ticketId: openId, body: $('support-body').value, clientMessageId: crypto.randomUUID() } },
  );
  $('support-reply').reset();
  await refresh();
};

// Live updates while the chat is open. A browser EventSource cannot send the
// token, so a one-minute stream ticket goes in the URL instead.
async function listen() {
  const { createStreamTicket } = await chat('mutation { createStreamTicket { path } }');
  const resume = lastEventId ? \`&lastEventId=\${encodeURIComponent(lastEventId)}\` : '';
  stream = new EventSource(\`https://api.revvly.ai\${createStreamTicket.path}\${resume}\`);
  stream.addEventListener('ready', (e) => {
    if (!JSON.parse(e.data).resumed) refresh().catch(console.error);
  });
  stream.addEventListener('ticket_updated', (e) => {
    lastEventId = e.lastEventId;
    $('support-typing').hidden = true;
    refresh().catch(console.error);
  });
  stream.addEventListener('typing', (e) => {
    const { ticketId, expiresInMs } = JSON.parse(e.data);
    if (ticketId !== openId) return;
    $('support-typing').hidden = false;
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => ($('support-typing').hidden = true), expiresInMs);
  });
  // The token behind the stream is ending, or the connection dropped: the
  // ticket is spent either way, so reconnect with a new one.
  const reconnect = (delay) => {
    stream.close();
    stream = null;
    if (!$('support').hidden) setTimeout(() => listen().catch(console.error), delay);
  };
  stream.addEventListener('reauthenticate', () => reconnect(0));
  stream.onerror = () => reconnect(5000);
}

$('support-toggle').onclick = async () => {
  const panel = $('support');
  panel.hidden = !panel.hidden;
  if (panel.hidden) {
    stream?.close();
    stream = null;
    return;
  }
  await refresh();
  await listen();
};`;

export function SupportWidgetGuide() {
  useDocsPage('Add a support chat to your app — Revvly Docs');
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Support chat widget</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Add a <em>support chat</em> to your app.
        </h1>
        <p className="dp-oneliner">
          From nothing to a working chat in your product: your signed-in users open requests and
          talk to your support team, and your agents answer from the Revvly Support inbox. One
          backend route, one small script, three settings.
        </p>
      </header>

      <section className="dp-section">
        <AnchorHeading id="order">The order to do things in</AnchorHeading>
        <ol className="dp-connects">
          <li>
            <Link to="/docs/guides/support-setup">Set up Support for your team</Link>: Revvly
            switches Support on for your workspace, then you check the prefix, roles, ticket types
            and notifications.
          </li>
          <li>
            <Link to="/docs/guides/support-knowledge">Add your knowledge</Link> so the AI has
            something to answer from (optional, and you can add it after launch).
          </li>
          <li>The five steps below: key, allowed origins, token route, chat client, widget.</li>
          <li>
            <Link to="/docs/guides/support-tokens">Sign users in to the support chat</Link> when you
            want roles, plans or bulk-synced users: every claim and the refresh rules.
          </li>
        </ol>
      </section>

      <section className="dp-section">
        <AnchorHeading id="prompt">Give this prompt to an integration agent</AnchorHeading>
        <p className="dp-section-sub">
          It covers every step on this page. Copied from the dashboard (Org Settings, Support,
          Secret keys) it also carries your tenant id.
        </p>
        <pre className="api-code">{AGENT_PROMPT}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="how-it-works">How the pieces fit</AnchorHeading>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Your backend</div>
            <div className="dp-field-v">
              Holds a support secret key and mints a one-hour token for whoever your session says is
              signed in.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">The chat in your page</div>
            <div className="dp-field-v">
              Gets the token from your backend and calls the{' '}
              <Link to="/docs/api/support-chat">support chat API</Link> with it, for that user only.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Your support team</div>
            <div className="dp-field-v">
              Sees each request in Support, unassigned until someone takes it, and replies from
              there.
            </div>
          </div>
        </div>
        <Figure
          title="A request from your app to your team"
          caption="Your backend vouches for the user and Revvly Support holds the conversation."
        >
          <Flow
            steps={[
              { title: 'Your backend', text: 'Mints a one-hour token for the signed-in user.' },
              { title: 'The chat in your page', text: 'Opens a request with that token.' },
              {
                title: 'Support inbox',
                text: 'The request waits unassigned until someone takes it.',
              },
              {
                title: 'Your team replies',
                text: 'The reply appears in your chat.',
                tone: 'accent',
              },
            ]}
          />
        </Figure>
      </section>

      <section className="dp-section">
        <AnchorHeading id="setup">Step 1: Turn it on in Revvly</AnchorHeading>
        <p className="dp-section-sub">
          Support is an add-on that Revvly switches on for your workspace, so Support has to appear
          in your sidebar first; if it does not, ask Revvly to turn it on. The key below is a
          Support key, created on the Support settings page. It is not the same as an API key under
          Settings, API Keys, and one cannot do the other&rsquo;s job.
        </p>
        <UiPath
          steps={['Org Settings', 'Support', 'Secret keys', 'Create secret key']}
          note="Admins and owners. The key is shown once: put it in your server's secret store, e.g. REVVLY_SUPPORT_SECRET_KEY."
        />
        <UiPath
          steps={['Org Settings', 'Support', 'General', 'Widget allowed origins']}
          note="Every site the chat runs on, e.g. https://app.example.com, plus http://localhost:3000 for development. Any other site is refused."
        />
        <p className="dp-section-sub">
          For many subdomains write <span className="api-inline">https://*.example.com</span>: it
          covers every subdomain at any depth but not <span className="api-inline">example.com</span>{' '}
          itself, another scheme or another port, so list the bare domain on its own line. A
          white-label domain is always its own line. Up to 100 lines.
        </p>
        <p className="dp-section-sub">
          If you want to pass support roles (for example founder or mentor), declare them first with
          the Roles button on the Knowledge page (Support, Knowledge): a role Revvly does not know
          is refused when you mint the token, so typos show up straight away.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="backend">Step 2: Add a token route to your backend</AnchorHeading>
        <p className="dp-section-sub">
          One route your signed-in pages call. It reads the user from your own session, never from
          the request, and returns a token for them. It reuses each user&rsquo;s token until a
          minute before it expires, so a user with five tabs open costs one sign-in an hour, not
          five per page load. Details on every field are in{' '}
          <Link to="/docs/guides/support-tokens">Sign users in to the support chat</Link>.
        </p>
        <pre className="api-code">{BACKEND}</pre>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Your secret key allows 50 sign-ins a second and 3,000 a minute across all your users.
            With tokens reused for their hour, that covers well over 100,000 sign-ins an hour. If
            you need more, raise them under Secret keys, Limits, which also shows the last 24 hours
            of traffic.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="client">Step 3: Add the chat client</AnchorHeading>
        <p className="dp-section-sub">
          A small helper that keeps the token in memory, refreshes it before it expires, and retries
          once with a newly minted token if the API says the token is no longer valid.
        </p>
        <pre className="api-code">{CLIENT}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="widget">Step 4: Add the chat to your page</AnchorHeading>
        <p className="dp-section-sub">
          A complete, unstyled chat: a list of the user&rsquo;s requests, a form to open one, the
          conversation and a reply box. Style it like the rest of your product, or use it as the
          shape of your own React or Vue component.
        </p>
        <span className="api-code-label">Markup</span>
        <pre className="api-code">{WIDGET_HTML}</pre>
        <span className="api-code-label">Install what the script imports</span>
        <pre className="api-code">{'npm install marked dompurify'}</pre>
        <p className="dp-plain">
          There is no Revvly package: the script talks to the chat API directly. It imports two
          libraries, <span className="api-inline">marked</span> and{' '}
          <span className="api-inline">dompurify</span>, to show the formatted replies the AI and
          your team write. Use your bundler as usual; if you would rather not add them, render every
          message with <span className="api-inline">textContent</span> and replies appear as plain
          text. Keep both up to date: the sanitiser is what stands between a reply and your page.
        </p>
        <span className="api-code-label">Script</span>
        <pre className="api-code">{WIDGET_JS}</pre>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            While the chat is open the script listens on the live stream and re-reads only when
            something changed, shows when your team is typing, and closes the stream when the chat
            is closed. Opening a conversation with unread replies marks it read, which also cancels
            the email Revvly would otherwise send about it. If your users talk to your backend
            rather than to Revvly, use the webhook instead:{' '}
            <Link to="/docs/api/support-chat#webhook">Live updates through your backend</Link>.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="test">Step 5: Test it</AnchorHeading>
        <UiPath
          steps={['Support', 'Inbox', 'Sandbox']}
          note="Chats through the same API as a practice customer, so you can see what your users will see."
        />
        <p className="dp-section-sub">
          Then try your own integration: open a request from your product, answer it from Support in
          the Revvly sidebar, and check that the reply appears in your chat, that the unread count
          clears once you open it, and that replying to a closed request reopens it.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="checklist">Before you go live</AnchorHeading>
        <ol className="dp-connects">
          <li>The secret key is only on your server: not in the page, logs, URLs or git.</li>
          <li>The token route mints only for the user your own session signed in.</li>
          <li>
            Every production site is in Widget allowed origins, the bare domain next to any
            wildcard.
          </li>
          <li>
            You pass email if users should hear about unread replies by email, and your chat
            settings let them turn those emails off.
          </li>
          <li>
            Your server reuses each user&rsquo;s token until it is about to expire, and the browser
            keeps it in memory only.
          </li>
          <li>
            The live stream closes with the chat, reconnects with a new ticket after an error, and
            backs off on RATE_LIMITED.
          </li>
          <li>
            Plain messages are shown as text (textContent). Messages with format MARKDOWN go through
            a sanitiser that allows only the documented tags and https links, never raw HTML.
          </li>
          <li>
            A message with notice AI_HANDOVER is shown as &ldquo;A teammate will reply&rdquo; rather
            than as a reply.
          </li>
        </ol>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/support-setup">Set up Support for your team ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/support-tokens">Sign users in to the support chat ›</Link>
          </li>
          <li>
            <Link to="/docs/api/support-chat">Support chat API reference ›</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
